// Per-account external links — the five hand-entered URLs on an Account, shown
// as a row of icons on the publishing calendar (one location selected), the
// composer and the inbox (Scott, 2026-09-30). Pure; the shared renderer is
// src/components/ui/AccountLinkIcons.tsx.
//
// ⚠️ A MISSING OR UNUSABLE URL PRODUCES NO ITEM — not a greyed icon, not a
// placeholder. A greyed icon reads as "broken"; the codebase rule is that an
// unresolvable value renders nothing.
// ⚠️ ABSOLUTE http(s) ONLY (isAbsoluteHttpUrl) — a stored "facebook.com/x" is a
// relative href that would navigate inside kyck.
//
// Google Ads (googleAdsUrl) joined the set in round 2 (2026-10-01). ℹ️
// `tiktokUrl` also exists on Account and is deliberately left out until asked
// for — one ORDER line and one pickAccountLinks line.
//
// ⚠️ THE ONE READ: surfaces that do not already carry these fields read them
// once per page from GET /api/accounts/links through useAccountLinks(), not by
// widening each surface's own projection. Carry it on the list when every row
// needs it; read it once for the page when many places need the same small
// thing.

import { isAbsoluteHttpUrl } from './absoluteHttpUrl.js';

import type { AccountExternalLinks } from '../types/account.js';
export type { AccountExternalLinks };

export type AccountLinkKey = keyof AccountExternalLinks;

export interface AccountLinkItem {
  key: AccountLinkKey;
  label: string;
  href: string;
}

const ORDER: Array<{ key: AccountLinkKey; label: string }> = [
  { key: 'websiteUrl', label: 'Website' },
  { key: 'facebookPageUrl', label: 'Facebook page' },
  { key: 'instagramUrl', label: 'Instagram' },
  { key: 'googleBusinessProfileUrl', label: 'Google Business profile' },
  { key: 'googleAdsUrl', label: 'Google Ads' },
  { key: 'fbAdAccountUrl', label: 'Meta ad account' },
];

/** Copy exactly the six link fields (never the whole Account) — the projection widening. */
export function pickAccountLinks(a: AccountExternalLinks): AccountExternalLinks {
  return {
    websiteUrl: a.websiteUrl ?? null,
    facebookPageUrl: a.facebookPageUrl ?? null,
    instagramUrl: a.instagramUrl ?? null,
    googleBusinessProfileUrl: a.googleBusinessProfileUrl ?? null,
    googleAdsUrl: a.googleAdsUrl ?? null,
    fbAdAccountUrl: a.fbAdAccountUrl ?? null,
  };
}

/** The icons to render, in a fixed order, omitting anything not safely linkable. */
export function accountLinkItems(links: Partial<AccountExternalLinks> | null | undefined): AccountLinkItem[] {
  if (!links) return [];
  const out: AccountLinkItem[] = [];
  for (const { key, label } of ORDER) {
    const raw = links[key];
    if (isAbsoluteHttpUrl(raw)) out.push({ key, label, href: raw!.trim() });
  }
  return out;
}

/**
 * Every account's link slice, keyed by id — the GET /api/accounts/links body.
 * Six nullable strings per account: a small payload even at full roster size,
 * which is what makes one read per page cheaper than six widened projections.
 */
export function accountLinksById(
  accounts: ReadonlyArray<AccountExternalLinks & { id: string }>,
): Record<string, AccountExternalLinks> {
  const out: Record<string, AccountExternalLinks> = {};
  for (const a of accounts) out[a.id] = pickAccountLinks(a);
  return out;
}
